/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

/**
 * Analytics event names - use these constants for type safety
 * Naming convention: entity_action (snake_case)
 */
export const AnalyticsEvents = {
  // Authentication Events
  AUTH_SIGNUP_STARTED: 'auth_signup_started',
  AUTH_SIGNUP_COMPLETED: 'auth_signup_completed',
  AUTH_LOGIN_STARTED: 'auth_login_started',
  AUTH_LOGIN_COMPLETED: 'auth_login_completed',
  AUTH_LOGIN_FAILED: 'auth_login_failed',
  AUTH_LOGOUT: 'auth_logout',
  AUTH_PASSWORD_RESET_REQUESTED: 'auth_password_reset_requested',
  AUTH_PASSWORD_RESET_COMPLETED: 'auth_password_reset_completed',
  AUTH_MFA_ENROLLED: 'auth_mfa_enrolled',
  AUTH_MFA_UNENROLLED: 'auth_mfa_unenrolled',
  AUTH_MFA_VERIFIED: 'auth_mfa_verified',

  // Project Events
  PROJECT_CREATED: 'project_created',
  PROJECT_VIEWED: 'project_viewed',
  PROJECT_UPDATED: 'project_updated',
  PROJECT_DELETED: 'project_deleted',

  // Clip Events
  CLIP_CREATED: 'clip_created',
  CLIP_VIEWED: 'clip_viewed',
  CLIP_UPDATED: 'clip_updated',
  CLIP_DELETED: 'clip_deleted',
  CLIP_METADATA_EDITED: 'clip_metadata_edited',
  CLIP_TAGS_EDITED: 'clip_tags_edited',
  CLIPS_SEARCHED: 'clips_searched',
  CLIPS_SORTED: 'clips_sorted',
  CLIPS_LINKED: 'clips_linked',
  CLIPS_COLUMNS_CHANGED: 'clips_columns_changed',
  CLIPS_IMPORTED: 'clips_imported',
  CLIPS_EXPORTED: 'clips_exported',
  CLIPS_BULK_TAGGED: 'clips_bulk_tagged',
  CLIPS_BULK_STATUS_UPDATED: 'clips_bulk_status_updated',
  CLIPS_ASSIGNED: 'clips_assigned',

  // VFX Shot Events
  VFX_SHOT_CREATED: 'vfx_shot_created',
  VFX_SHOT_VIEWED: 'vfx_shot_viewed',
  VFX_SHOT_UPDATED: 'vfx_shot_updated',
  VFX_SHOT_DELETED: 'vfx_shot_deleted',
  VFX_SHOT_STATUS_CHANGED: 'vfx_shot_status_changed',
  VFX_SHOTS_ASSIGNED: 'vfx_shots_assigned',
  VFX_SHOTS_DETECTED: 'vfx_shots_detected',

  // Comment Events
  COMMENT_CREATED: 'comment_created',
  COMMENT_UPDATED: 'comment_updated',
  COMMENT_DELETED: 'comment_deleted',

  // Team Events
  TEAM_MEMBER_INVITED: 'team_member_invited',
  TEAM_MEMBER_JOINED: 'team_member_joined',
  TEAM_MEMBER_ROLE_CHANGED: 'team_member_role_changed',
  TEAM_MEMBER_REMOVED: 'team_member_removed',

  // Timeline Events
  TIMELINE_COMMIT_CREATED: 'timeline_commit_created',
  TIMELINE_COMMIT_VIEWED: 'timeline_commit_viewed',
  COMMIT_VIEWED: 'commit_viewed',
  COMMIT_DELETED: 'commit_deleted',
  COMMITS_COMPARED: 'commits_compared',
  TIMELINE_VIEW_TOGGLED: 'timeline_view_toggled',
  TIMELINE_COMMIT_DELETED: 'timeline_commit_deleted',
  TIMELINE_COMPARISON_CREATED: 'timeline_comparison_created',
  TIMELINE_COMPARISON_VIEWED: 'timeline_comparison_viewed',

  // Navigation/Page Events (auto-tracked)
  PAGE_VIEW: '$pageview',
  PAGE_LEAVE: '$pageleave',

  // Feature Usage Events
  FEATURE_USED: 'feature_used',
  SEARCH_PERFORMED: 'search_performed',
  FILTER_APPLIED: 'filter_applied',
  EXPORT_INITIATED: 'export_initiated',

  // Activity Feed Events
  ACTIVITY_ITEM_CLICKED: 'activity_item_clicked',

  // Shared Detail Events (Clip/VFX)
  TEAM_MEMBER_ASSIGNED: 'team_member_assigned',
  TAG_ADDED: 'tag_added',

  // Transcode Events
  TRANSCODE_UPLOADED: 'transcode_uploaded',
  TRANSCODE_VIEWED: 'transcode_viewed',
  TRANSCODE_DELETED: 'transcode_deleted',
  TRANSCODE_COMPLETED: 'transcode_completed',

  // Team/Profile Events
  MFA_ENABLED: 'mfa_enabled',
  PROFILE_EDITED: 'profile_edited',

  // Tag Events
  TAG_CREATED: 'tag_created',
  TAG_EDITED: 'tag_edited',
  TAG_DELETED: 'tag_deleted',

  // VFX Settings Events
  VFX_MARKER_COLOR_SELECTED: 'vfx_marker_color_selected',
  VFX_SETTINGS_SAVED: 'vfx_settings_saved',

  // Navigation Events
  THEME_CHANGED: 'theme_changed',
  SIDEBAR_TOGGLED: 'sidebar_toggled',
  PROJECT_SWITCHED: 'project_switched',
  NAVIGATION_CLICKED: 'navigation_clicked',
} as const

export type AnalyticsEventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents]
